import { useCallback, useEffect, useMemo, useState } from 'react';
import { CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { TRANSFORMERS } from '@lexical/markdown';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { $isRootTextContentEmpty } from '@lexical/text';
import { $dfs } from '@lexical/utils';
import { createCommand, EditorState, LexicalCommand } from 'lexical';
import debounce from 'lodash/debounce';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Stack, Typography } from '@lyearn/ui';
import { MentionEntityTypeEnum, MentionType } from '@/types/schema';

import { AudioNode } from './nodes/AudioNode';
import { FileNode } from './nodes/FileNode';
import { ImageNode } from './nodes/ImageNode';
import { $isMentionNode, MentionNode } from './nodes/MentionNode';
import { UploadPlaceholderNode } from './nodes/UploadPlaceholderNode';
import { VideoNode } from './nodes/VideoNode';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import DraggableBlockPlugin from './plugins/DraggableBlockPlugin';
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin';
import FloatingTextFormatToolbarPlugin from './plugins/FloatingTextFormatToolbarPlugin';
import { HTMLCompatibilityPlugin } from './plugins/HTMLCompatibilityPlugin';
import MentionsPlugin from './plugins/MentionsPlugin';
import StyleInjector from './plugins/StyleInjector';
import { TabIndentationPlugin } from './plugins/TabIndentationPlugin';
import UppyPlugin from './plugins/UppyPlugin';
import { theme } from './styles';

export interface InsertImagePayload {
  src: string;
  altText: string;
}

export interface InsertVideoPayload {
  src: string;
}

export interface InsertFilePayload {
  src: string;
  name: string;
}

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> = createCommand();
export const INSERT_VIDEO_COMMAND: LexicalCommand<InsertVideoPayload> = createCommand();
export const INSERT_FILE_COMMAND: LexicalCommand<InsertFilePayload> = createCommand();

function onError(error: Error) {
  console.error(error);
}

interface LexicalEditorProps {
  className?: string;
  value?: string;
  onUpdate?: (data: {
    text: string;
    media: any[];
    mentions: MentionType[];
    isEmpty?: boolean;
  }) => void;
  autoSave?: boolean;
  classes?: {
    mediaPlugin?: string;
  };
  debouncedDuration?: number;
  disableEditor?: boolean;
  editorClassName?: string;
  placeholder?: string;
  autoFocus?: boolean;
}

function LexicalEditor({
  value,
  onUpdate,
  autoSave = true,
  className,
  disableEditor = false,
  classes,
  debouncedDuration = 5000,
  editorClassName,
  placeholder,
  autoFocus = false,
}: LexicalEditorProps) {
  const { t } = useTranslation();
  const initialConfig = {
    namespace: 'MyEditor',
    theme,
    onError,
    editable: !disableEditor,
    editorState: (editor: any) => {
      if (value) {
        try {
          const parsedValue = JSON.parse(value);
          editor.setEditorState(editor.parseEditorState(parsedValue));
        } catch (e) {
          console.error('Error parsing initial editor state:', e);
        }
      }
    },
    nodes: [
      AutoLinkNode,
      ListNode,
      ListItemNode,
      HeadingNode,
      QuoteNode,
      CodeNode,
      LinkNode,
      HorizontalRuleNode,
      VideoNode,
      AudioNode,
      ImageNode,
      FileNode,
      UploadPlaceholderNode,
      MentionNode,
    ],
  };

  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

  const update = useEvent((editorState: EditorState) => {
    if (!onUpdate) return;
    const json = editorState.toJSON();
    const jsonString = JSON.stringify(json);
    if (jsonString === value) {
      return;
    }

    const mentions: MentionType[] = [];
    let isEmpty = false;

    editorState.read(() => {
      const allNodes = $dfs();
      allNodes.forEach(({ node }) => {
        if ($isMentionNode(node) && node.__userId) {
          mentions.push({
            entityId: node.__userId,
            entityType: MentionEntityTypeEnum.User,
          });
        }
      });

      // `false` indicates not in composition mode; second arg (true) trims whitespace.
      isEmpty = $isRootTextContentEmpty(false, true);
    });

    onUpdate({
      text: jsonString,
      media: [], // Currently not tracking media separately
      mentions,
      isEmpty,
    });
  });

  const debouncedUpdate = useMemo(
    () =>
      debounce((editorState: EditorState) => {
        update(editorState);
      }, debouncedDuration),
    [debouncedDuration, update],
  );

  const onChange = useCallback(
    (editorState: EditorState) => {
      if (autoSave) {
        debouncedUpdate(editorState);
      }
    },
    [autoSave, debouncedUpdate],
  );

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  useEffect(() => {
    return () => {
      // console.log('LexicalEditor unmounted, flushing debounced updates');
      debouncedUpdate.flush();
    };
  }, [debouncedUpdate]);

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <StyleInjector />
      <RichTextPlugin
        ErrorBoundary={LexicalErrorBoundary}
        contentEditable={
          <Box
            ref={onRef}
            className={cn(' w-full relative', className, { 'min-h-[120px]': !disableEditor })}>
            <ContentEditable
              aria-placeholder={placeholder || t('Common:AddText')}
              className={cn('w-full h-full rounded-12', editorClassName, {
                'min-h-[120px]  p-8': !disableEditor,
              })}
              placeholder={
                <Stack className="absolute top-8 left-8 pointer-events-none">
                  <Typography className="text-text-placeholder" variant="body-long-01">
                    {placeholder || t('Common:AddText')}
                  </Typography>
                </Stack>
              }
            />
          </Box>
        }
      />
      <OnChangePlugin onChange={onChange} />
      <HistoryPlugin />
      {autoFocus ? <AutoFocusPlugin /> : null}
      {floatingAnchorElem ? (
        <>
          <DraggableBlockPlugin anchorElem={floatingAnchorElem} />
          <FloatingLinkEditorPlugin
            anchorElem={floatingAnchorElem}
            isLinkEditMode={isLinkEditMode}
            setIsLinkEditMode={setIsLinkEditMode}
          />
          {disableEditor ? null : (
            <FloatingTextFormatToolbarPlugin
              anchorElem={floatingAnchorElem}
              setIsLinkEditMode={setIsLinkEditMode}
            />
          )}
        </>
      ) : null}
      <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
      <ListPlugin />
      <HTMLCompatibilityPlugin key="html-compatibility" initialContent={value} />
      <UppyPlugin className={classes?.mediaPlugin} />
      <LinkPlugin />
      <ClickableLinkPlugin />
      <AutoLinkPlugin />
      <TabIndentationPlugin maxIndent={4} />
      <MentionsPlugin />
    </LexicalComposer>
  );
}

export default LexicalEditor;
